import type { CSSProperties, ReactNode } from "react";
import { Badge, Box, Button, Card, Group, Stack, Table, Text, Title } from "@mantine/core";
import { IconCheck, IconClock } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { formatDhivehiDate } from "@hmh-proc/shared";
import {
  approvalDisplayName,
  dhivehiDisplayStyle,
  farumaFieldDisplayStyle,
} from "../../../utils/dhivehiText";

/** Building blocks for showing a bid committee form on screen the way its printed template lays it out. */

const headingStyle: CSSProperties = { ...farumaFieldDisplayStyle(), fontWeight: 700, textDecoration: "underline" };

export function DhivehiText({ value, multiline }: { value: string | null | undefined; multiline?: boolean }) {
  const text = value?.trim() ? value : "-";
  return (
    <Text style={{ ...(multiline ? { whiteSpace: "pre-wrap" } : {}), ...dhivehiDisplayStyle(text) }}>{text}</Text>
  );
}

export function LtrText({ value }: { value: string | null | undefined }) {
  return (
    <Text component="span" dir="ltr">
      {value?.trim() ? value : "-"}
    </Text>
  );
}

/** Centred form title, an optional second line, and the form's number below it. */
export function FormTitle({ title, subtitle, number }: { title: string; subtitle?: string; number?: string | null }) {
  return (
    <Stack gap={4} align="center">
      <Title order={3} style={{ ...farumaFieldDisplayStyle(), fontWeight: 700, textAlign: "center" }}>
        {title}
      </Title>
      {subtitle && (
        <Text fw={700} style={{ ...farumaFieldDisplayStyle(), textAlign: "center" }}>
          {subtitle}
        </Text>
      )}
      {number && (
        <Text c="dimmed" dir="ltr">
          {number}
        </Text>
      )}
    </Stack>
  );
}

/** A label/value row, or a full-width block with the label above its text. */
export type FormFieldRow = { label: string; value: ReactNode; block?: boolean };

export function FormFieldsTable({ rows }: { rows: FormFieldRow[] }) {
  return (
    <Table withTableBorder withColumnBorders style={{ tableLayout: "fixed" }}>
      <Table.Tbody>
        {rows.map((row) =>
          row.block ? (
            <Table.Tr key={row.label}>
              <Table.Td colSpan={2} style={{ verticalAlign: "top" }}>
                <Text style={headingStyle}>{row.label}</Text>
                {row.value}
              </Table.Td>
            </Table.Tr>
          ) : (
            <Table.Tr key={row.label}>
              <Table.Td style={{ ...farumaFieldDisplayStyle(), verticalAlign: "top", width: "30%" }}>
                {row.label}
              </Table.Td>
              <Table.Td style={{ verticalAlign: "top" }}>{row.value}</Table.Td>
            </Table.Tr>
          )
        )}
      </Table.Tbody>
    </Table>
  );
}

export type MemberVote = {
  id: string;
  status: string;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  comments?: string | null;
  member?: { fullName?: string | null; fullNameDhivehi?: string | null } | null;
};

/** A member's decision goes in the approve or reject column, with the date they voted. */
function DecisionCell({ vote, decision, mark }: { vote: MemberVote; decision: "APPROVED" | "REJECTED"; mark: string }) {
  if (vote.status !== decision) return null;
  const approved = decision === "APPROVED";
  return (
    <Stack gap={2} align="center">
      <Text c={approved ? "green" : "red"} fw={700} style={farumaFieldDisplayStyle()}>
        {mark}
      </Text>
      <Text size="xs" c="dimmed" style={farumaFieldDisplayStyle()}>
        {formatDhivehiDate(approved ? vote.approvedAt : vote.rejectedAt)}
      </Text>
      {!approved && vote.comments && (
        <Text size="xs" style={dhivehiDisplayStyle(vote.comments)}>
          {vote.comments}
        </Text>
      )}
    </Stack>
  );
}

/** The acknowledgement text followed by the members' names and their approve / reject columns. */
export function MemberVotesTable({
  heading,
  text,
  columns,
  marks,
  office,
  votes,
}: {
  heading: string;
  text: string;
  columns: { name: string; office: string; approve: string; reject: string };
  marks: { approve: string; reject: string };
  office?: string;
  votes: MemberVote[];
}) {
  const th = farumaFieldDisplayStyle();
  return (
    <div>
      <Box p="sm" style={{ border: "1px solid var(--mantine-color-default-border)", borderBottom: "none" }}>
        <Text style={headingStyle}>{heading}</Text>
        <Text style={{ ...farumaFieldDisplayStyle(), whiteSpace: "pre-wrap", textAlign: "justify" }}>{text}</Text>
      </Box>
      <Table withTableBorder withColumnBorders style={{ tableLayout: "fixed" }}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ ...th, width: "34%" }}>{columns.name}</Table.Th>
            <Table.Th style={th}>{columns.office}</Table.Th>
            <Table.Th style={{ ...th, textAlign: "center" }}>{columns.approve}</Table.Th>
            <Table.Th style={{ ...th, textAlign: "center" }}>{columns.reject}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {votes.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={4}>
                <Text size="sm" c="dimmed" dir="ltr" ta="center">
                  Members are listed once the form is sent for approval.
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            votes.map((v) => (
              <Table.Tr key={v.id}>
                <Table.Td>
                  <DhivehiText value={approvalDisplayName(v.member)} />
                </Table.Td>
                <Table.Td>{office && <DhivehiText value={office} />}</Table.Td>
                <Table.Td>
                  <DecisionCell vote={v} decision="APPROVED" mark={marks.approve} />
                </Table.Td>
                <Table.Td>
                  <DecisionCell vote={v} decision="REJECTED" mark={marks.reject} />
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
    </div>
  );
}

/** Chairperson / secretary box: their name, and whether they have signed. */
export function OfficerBox({
  title,
  labels,
  name,
  signed,
  signedAt,
  rejected,
  action,
}: {
  title: string;
  labels: { name: string; signature: string; rejected: string };
  name: string;
  signed: boolean;
  signedAt?: string | null;
  rejected: boolean;
  action?: ReactNode;
}) {
  let status: ReactNode = (
    <Badge color="gray" variant="light">
      Pending
    </Badge>
  );
  if (signed) {
    status = (
      <Badge color="green" variant="light" leftSection={<IconCheck size={12} />} style={{ textTransform: "none" }}>
        {signedAt ? formatDhivehiDate(signedAt) : "Signed"}
      </Badge>
    );
  } else if (rejected) {
    status = (
      <Text c="red" fw={700} style={farumaFieldDisplayStyle()}>
        {labels.rejected}
      </Text>
    );
  }

  return (
    <Table withTableBorder withColumnBorders style={{ tableLayout: "fixed" }}>
      <Table.Tbody>
        <Table.Tr>
          <Table.Td colSpan={2} style={headingStyle}>
            {title}
          </Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Td style={{ width: "50%" }}>
            <Group gap="xs" wrap="nowrap">
              <Text style={farumaFieldDisplayStyle()}>{labels.name}</Text>
              <DhivehiText value={name === "—" ? null : name} />
            </Group>
          </Table.Td>
          <Table.Td style={{ width: "50%" }}>
            <Group gap="sm">
              <Text style={farumaFieldDisplayStyle()}>{labels.signature}</Text>
              {status}
              {action}
            </Group>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
    </Table>
  );
}

/** Shown to a signer whose approval is still pending: they act on it from My Approvals. */
export function MyApprovalsHint({ what }: { what: string }) {
  const navigate = useNavigate();
  return (
    <Card>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Use <strong>My Approvals</strong> to approve or reject this {what}.
        </Text>
        <Button
          variant="light"
          leftSection={<IconClock size={16} />}
          onClick={() => navigate("/tenders/pending-approvals")}
        >
          Go to My Approvals
        </Button>
      </Group>
    </Card>
  );
}

/** Committee members as printed: never the secretary, and the chair only when their vote is included. */
export function membersListedOnForm<T extends { memberId: string }>(
  votes: T[],
  opts: { chairId?: string; secretaryId?: string; chairVotes: boolean }
): T[] {
  return votes.filter(
    (v) => v.memberId !== opts.secretaryId && (opts.chairVotes || v.memberId !== opts.chairId)
  );
}
